/**
 * ARVEXIS Dream Conversation Component
 * =====================================
 * 
 * Core Principle: Arvexis is not trying to interpret the dream.
 *                Arvexis is trying to understand the dreamer.
 * 
 * This component powers the guided dream conversation after interpretation.
 * It supports both text and voice input, maintaining the calm, reflective
 * conversation style throughout.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Send, Loader2, Pause, X, ChevronDown, Square, Volume2 } from 'lucide-react';
import { ArvexisSymbol } from './ArvexisSymbol';
import { VoiceWaveform } from './VoiceWaveform';
import { ACTIVITY, activityLabel } from '../utils/activityVocabulary';
import { unlockAudioContext, claimSpokenSession, stopPlayback, currentPlayback, playScheduledChunks, playSequentialChunks } from '../utils/listenPlayback';
// Dream/Reflection-owned playback floor — isolated from Message's session. Registering here is what
// stops a reflection and a dream reply speaking over each other.
import {
  claimFloor,
  currentFloor,
  isCurrentFloor,
  setFloorState,
  releaseFloor,
  subscribeFloor
} from '../utils/dreamReflectionPlayback';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Read-aloud here is the SAME path Message read-aloud uses: /api/companion/tts/plan splits her
// reply at sentence boundaries, /api/companion/tts synthesizes each chunk, and the shared
// listenPlayback utilities schedule them gaplessly on the Web Audio clock (with the element
// fallback where Web Audio is unavailable). No new provider, model or audio architecture, and the
// voice is still the member's one canonical selection, resolved server-side.
const SILENT_WAV = 'data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YRAAAAAAAAAAAAAAAAAAAAAAAAAA';
const LISTEN_SPEED = 0.92;     // the same rendering settings Message read-aloud uses
const LISTEN_VOLUME = 0.7;

// The access token lives 15 minutes and reading a reflection before replying easily
// outlasts it. Without this, a late message returned 401 and the panel simply went quiet,
// which is what "the conversation got stuck" looked like. Same pattern as Chat.
const fetchWithAuthRetry = async (url, options) => {
  let response = await fetch(url, options);
  if (response.status === 401) {
    const refreshed = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include'
    });
    if (refreshed.ok) {
      response = await fetch(url, options);
    }
  }
  return response;
};

// =============================================================================
// TYPING INDICATOR — Shows Arvexis is thinking
// =============================================================================
const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-4 py-3">
    <span className="text-xs text-[#F4EFE7]/40 mr-2" style={{ fontFamily: '"Inter", sans-serif' }}>
      Arvexis is reflecting
    </span>
    <motion.span
      className="w-1.5 h-1.5 rounded-full bg-[#E8C58F]/60"
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 1.2, repeat: Infinity, delay: 0 }}
    />
    <motion.span
      className="w-1.5 h-1.5 rounded-full bg-[#E8C58F]/60"
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 1.2, repeat: Infinity, delay: 0.2 }}
    />
    <motion.span
      className="w-1.5 h-1.5 rounded-full bg-[#E8C58F]/60"
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
    />
  </div>
);

// =============================================================================
// MESSAGE BUBBLE — Individual conversation message
// =============================================================================
const MessageBubble = ({ message, isArvexis, lang, onListen, isSpeaking, isPreparing }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex ${isArvexis ? 'justify-start' : 'justify-end'} mb-4`}
    >
      <div
        className={`max-w-[85%] px-4 py-3 rounded-2xl ${
          isArvexis 
            ? 'bg-white/5 border border-white/10 rounded-tl-sm' 
            : 'bg-[#E8C58F]/15 border border-[#E8C58F]/30 rounded-tr-sm'
        }`}
      >
        {isArvexis && (
          <div className="flex items-center gap-2 mb-2">
            <ArvexisSymbol size={20} />
            <span className="text-[10px] text-[#E8C58F] font-medium tracking-wider">ARVEXIS</span>
            {/* Listen — tap again to stop, exactly as read-aloud behaves in Message. It sits in
                the existing attribution row so it never competes with what she said. */}
            {onListen && (
              <button
                type="button"
                onClick={onListen}
                aria-label={
                  isPreparing
                    ? (lang === 'es' ? 'Preparando voz' : 'Preparing voice')
                    : isSpeaking
                      ? (lang === 'es' ? 'Detener' : 'Stop')
                      : (lang === 'es' ? 'Escuchar' : 'Listen')
                }
                title={
                  isPreparing
                    ? (lang === 'es' ? 'Preparando voz' : 'Preparing voice')
                    : isSpeaking
                      ? (lang === 'es' ? 'Reproduciendo — toca para detener' : 'Playing — tap to stop')
                      : (lang === 'es' ? 'Escuchar' : 'Listen')
                }
                className={`ml-auto -mr-1 p-1 rounded-lg transition-colors ${
                  isSpeaking || isPreparing
                    ? 'text-[#E8C58F]'
                    : 'text-[#F4EFE7]/35 hover:text-[#F4EFE7]/70'
                }`}
                data-testid="dream-conversation-listen"
                data-listen-state={isPreparing ? 'preparing' : isSpeaking ? 'speaking' : 'idle'}
              >
                <Volume2 size={14} strokeWidth={1.6} />
              </button>
            )}
          </div>
        )}
        {isArvexis && isPreparing && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.45, 0.9, 0.45] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="block mb-1 text-[11px] text-[#E8C58F]/80"
            data-testid="dream-conversation-listen-preparing"
          >
            {lang === 'es' ? 'Preparando voz…' : 'Preparing voice…'}
          </motion.span>
        )}
        <p
          className="text-[16px] leading-[1.65]"
          style={{ 
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            color: isArvexis ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.94)',
            letterSpacing: '0.001em'
          }}
        >
          {message.content}
        </p>
        {message.source === 'voice' && !isArvexis && (
          <div className="flex items-center gap-1 mt-2">
            <Mic size={10} className="text-[#F4EFE7]/30" />
            <span className="text-[10px] text-[#F4EFE7]/30">Voice</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// =============================================================================
// EXPLORE PROMPT — Initial "Would you like to explore?" prompt
// =============================================================================
const ExplorePrompt = ({ lang, onExplore, onMaybeLater }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center text-center px-6 py-8"
    >
      {/* Arvexis Logo */}
      <div className="mb-6">
        <motion.div
          animate={{ 
            scale: [1, 1.02, 1],
            opacity: [0.8, 1, 0.8]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArvexisSymbol size={48} />
        </motion.div>
      </div>

      <p 
        className="text-lg mb-2 text-[#F4EFE7]/90"
        style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic' }}
      >
        {lang === 'es' 
          ? '¿Te gustaría explorar este sueño juntos?' 
          : 'Would you like to explore this dream together?'}
      </p>
      
      <p 
        className="text-sm text-[#F4EFE7]/50 mb-8 max-w-xs"
        style={{ fontFamily: '"Inter", sans-serif' }}
      >
        {lang === 'es'
          ? 'Una conversación para explorar lo que más te llamó la atención.'
          : 'A conversation to explore what stood out to you.'}
      </p>

      <div className="flex gap-3">
        <button
          onClick={onMaybeLater}
          className="px-5 py-2.5 rounded-full text-sm text-[#F4EFE7]/60 hover:text-[#F4EFE7] transition-all"
          style={{ 
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
          data-testid="conversation-maybe-later"
        >
          {lang === 'es' ? 'Quizás después' : 'Maybe Later'}
        </button>
        <button
          onClick={onExplore}
          className="px-5 py-2.5 rounded-full text-sm font-medium transition-all"
          style={{ 
            background: 'rgba(232, 197, 143, 0.06)',
            border: '1px solid rgba(232, 197, 143, 0.56)',
            color: '#F4EFE7'
          }}
          data-testid="conversation-explore"
        >
          {lang === 'es' ? 'Explorar Juntos' : 'Explore Together'}
        </button>
      </div>
    </motion.div>
  );
};

// =============================================================================
// RESUME PROMPT — "Would you like to continue?" prompt
// =============================================================================
const ResumePrompt = ({ lang, message, onContinue, onStartFresh }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center text-center px-6 py-8"
    >
      <div className="mb-6">
        <ArvexisSymbol size={40} />
      </div>

      <p 
        className="text-[16px] leading-[1.65] mb-6 text-[#F4EFE7]/90 max-w-sm"
        style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}
      >
        {message}
      </p>

      <div className="flex gap-3">
        <button
          onClick={onStartFresh}
          className="px-5 py-2.5 rounded-full text-sm text-[#F4EFE7]/60 hover:text-[#F4EFE7] transition-all"
          style={{ 
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
          data-testid="conversation-start-fresh"
        >
          {lang === 'es' ? 'Empezar de nuevo' : 'Start Fresh'}
        </button>
        <button
          onClick={onContinue}
          className="px-5 py-2.5 rounded-full text-sm font-medium transition-all"
          style={{ 
            background: 'rgba(232, 197, 143, 0.06)',
            border: '1px solid rgba(232, 197, 143, 0.56)',
            color: '#F4EFE7'
          }}
          data-testid="conversation-continue"
        >
          {lang === 'es' ? 'Continuar' : 'Continue'}
        </button>
      </div>
    </motion.div>
  );
};

// =============================================================================
// MAIN DREAM CONVERSATION COMPONENT
// =============================================================================
const DreamConversation = ({ 
  dreamId, 
  lang = 'en',
  onClose,
  onConversationEnd 
}) => {
  // Conversation state
  const [phase, setPhase] = useState('prompt'); // 'prompt' | 'resume' | 'active' | 'ended'
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  // Voice here works exactly as the Message composer does: stopping a recording ENDS CAPTURE and
  // puts the transcript in the composer to be read, edited or discarded. It never sends. A
  // spoken sentence is not a decision to send it.
  const [recordingPhase, setRecordingPhase] = useState(null); // 'recording' | 'transcribing'
  const [composerFromVoice, setComposerFromVoice] = useState(false);
  const [resumeMessage, setResumeMessage] = useState('');
  // Every failure in this panel used to go to the console only, so a dead turn looked
  // like Arvexis had stopped answering. Failures are now visible and retryable.
  const [failure, setFailure] = useState(null);
  const [lastAttempt, setLastAttempt] = useState(null);
  // Live capture level, so the recording state reads as a restrained waveform rather
  // than a status line. Same metering the Message composer uses.
  const [audioLevel, setAudioLevel] = useState(0);
  // Read-aloud state, mirroring Message: preparing is honestly distinct from speaking.
  const [speakingIndex, setSpeakingIndex] = useState(-1);
  const [preparingIndex, setPreparingIndex] = useState(-1);
  const [listenFailed, setListenFailed] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isMountedRef = useRef(true);
  const listenAudioRef = useRef(null);
  const listenRef = useRef({ token: 0, abort: null });
  // The Dream/Reflection floor generation this mount last claimed.
  const floorRef = useRef(0);

  // Read-aloud now follows the member out of the dream (Founder-approved, October 2026): the player
  // and the floor live above this mount. Stopping is an explicit act — a second tap, a new
  // playback claiming the floor, the end of the sequence, or a failure.
  const stopListen = useCallback(() => {
    const s = listenRef.current;
    s.token += 1;
    if (s.abort) { s.abort.abort(); s.abort = null; }
    // The one owner stops what is speaking, whichever surface started it.
    stopPlayback();
    listenAudioRef.current = null;
    // And the Dream/Reflection floor goes idle, so the Reflection screen's controls do too.
    releaseFloor(floorRef.current);
    if (isMountedRef.current) {
      setSpeakingIndex(-1);
      setPreparingIndex(-1);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Deliberately nothing else: unmounting unsubscribes this mount's UI and must no longer
      // abort the request, stop the player or unschedule what is already queued.
    };
  }, []);

  // A returning mount reads the floor instead of trusting its own state from before it left.
  useEffect(() => {
    const apply = (floor) => {
      if (!isMountedRef.current) return;
      if (floor.owner !== 'dream' || typeof floor.itemKey !== 'number') {
        setSpeakingIndex(-1);
        setPreparingIndex(-1);
        return;
      }
      if (floor.state === 'loading') {
        setPreparingIndex(floor.itemKey);
        setSpeakingIndex(-1);
      } else if (floor.state === 'playing') {
        setSpeakingIndex(floor.itemKey);
        setPreparingIndex(-1);
      } else {
        setSpeakingIndex(-1);
        setPreparingIndex(-1);
      }
    };
    apply(currentFloor());
    return subscribeFloor(apply);
  }, []);

  const speakArvexis = useCallback(async (text, index) => {
    if (!isMountedRef.current || !text?.trim()) return;

    // A second tap on the same response cancels instead of stacking another synthesis,
    // and tapping a different response replaces the one that is playing.
    if (speakingIndex === index || preparingIndex === index) {
      stopListen();
      return;
    }
    stopListen();
    setListenFailed(false);

    // Unlock audio inside the tap itself (iOS Safari): the Web Audio clock that keeps the
    // pauses deliberate, and an element as the fallback path.
    const ctx = unlockAudioContext();
    const player = new Audio(SILENT_WAV);
    player.volume = LISTEN_VOLUME;
    player.preload = 'auto';
    player.play().catch(() => {});
    listenAudioRef.current = player;
    // ONE ARVEXIS SPOKEN-MEDIA SESSION (Founder ruling, 2026-10-08). A reflection and a Message
    // read aloud are the same voice, so claiming the session here stops whatever was speaking —
    // including a Read Aloud still running in a conversation the member has left. Dream synthesis
    // and Dream content are untouched; this is only who holds the floor.
    claimSpokenSession({ player, surface: 'dream' });

    const controller = new AbortController();
    listenRef.current.abort = controller;
    const myToken = listenRef.current.token;
    // ONE DREAM/REFLECTION VOICE (Founder TestFlight, October 2026). Claiming the floor stops a
    // reflection that is still speaking on the screen above this panel. The stop closure holds the
    // request, not this mount, so it still works once the member has navigated away. It refuses to
    // touch the shared session unless Dream is still the surface holding it, so Message is never
    // stopped by anything in here.
    const myFloor = claimFloor({
      owner: 'dream',
      itemKey: index,
      abort: controller,
      stop: () => {
        try { controller.abort(); } catch (e) { /* already settled */ }
        if (currentPlayback().surface === 'dream') stopPlayback();
      }
    });
    floorRef.current = myFloor;
    const alive = () => isCurrentFloor(myFloor) && listenRef.current.token === myToken;

    setPreparingIndex(index);
    const chunkCache = new Map();
    let planChunks = [];

    const getAudio = (i) => {
      if (!chunkCache.has(i)) {
        chunkCache.set(i, (async () => {
          const res = await fetchWithAuthRetry(`${API_URL}/api/companion/tts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            signal: controller.signal,
            body: JSON.stringify({
              text: planChunks[i].text,
              speed: LISTEN_SPEED,
              language: lang,
              continuation: !!planChunks[i].continuation
            })
          });
          if (!res.ok) throw new Error(`chunk_http_${res.status}`);
          const data = await res.json();
          return data.audio;
        })());
      }
      return chunkCache.get(i);
    };

    try {
      const planRes = await fetchWithAuthRetry(`${API_URL}/api/companion/tts/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({ text, language: lang })
      });
      if (!alive()) return;
      if (!planRes.ok) throw new Error(`plan_http_${planRes.status}`);
      const plan = await planRes.json();
      planChunks = plan.chunks || [];
      if (!planChunks.length) { stopListen(); return; }

      const onFirstAudio = () => {
        setFloorState(myFloor, 'playing');
        if (isMountedRef.current) {
          setPreparingIndex(-1);
          setSpeakingIndex(index);
        }
      };

      const result = ctx
        ? await playScheduledChunks({
            ctx, chunks: planChunks, getAudio, volume: LISTEN_VOLUME, isAlive: alive, onFirstAudio
          })
        : await playSequentialChunks({ player, chunks: planChunks, getAudio, isAlive: alive, onFirstAudio });

      if (!alive() || result.cancelled) return;
      // L1-A CONSEQUENCE — `listenPlayback` is shared, and a chunk failure is now RETURNED
      // instead of thrown so whatever was already spoken is kept. Without this branch the
      // failure would fall straight through to stopListen() and Dream read-aloud would die
      // silently, which it must never do. Same outcome as before, reached differently.
      if (result.failure) {
        if (result.failure.aborted) return;
        console.error('Dream conversation listen failed:', result.failure.message);
        stopListen();
        setListenFailed(true);
        return;
      }
      stopListen();
    } catch (error) {
      if (error?.name === 'AbortError' || !alive()) return;
      console.error('Dream conversation listen failed:', error);
      stopListen();
      // Honest failure: read-aloud never dies silently and her voice is never substituted.
      setListenFailed(true);
    }
  }, [lang, speakingIndex, preparingIndex, stopListen]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Start conversation
  const startConversation = async () => {
    setIsLoading(true);
    setFailure(null);
    
    try {
      const response = await fetchWithAuthRetry(`${API_URL}/api/conversation/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ dream_id: dreamId, lang })
      });

      if (!response.ok) throw new Error(`start failed (${response.status})`);
      
      const data = await response.json();
      setSessionId(data.session_id);
      
      if (data.status === 'resumed') {
        // Show resume prompt
        setResumeMessage(data.message);
        setMessages(data.history || []);
        setPhase('resume');
      } else {
        // New conversation - show opening message
        setMessages([{ role: 'arvexis', content: data.message }]);
        setPhase('active');
      }
    } catch (error) {
      console.error('Start conversation error:', error);
      setFailure('start');
      setLastAttempt({ type: 'start' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle "Explore Together" click
  const handleExplore = () => {
    startConversation();
  };

  // Handle "Maybe Later" click
  const handleMaybeLater = () => {
    onClose?.();
  };

  // Handle "Continue" click (resume)
  const handleContinue = () => {
    // Add the welcome back message to the conversation
    setMessages(prev => [...prev, { role: 'arvexis', content: resumeMessage }]);
    setPhase('active');
  };

  // Handle "Start Fresh" click
  const handleStartFresh = async () => {
    // End the old session and start new
    if (sessionId) {
      await fetchWithAuthRetry(`${API_URL}/api/conversation/end/${sessionId}`, {
        method: 'POST',
        credentials: 'include'
      }).catch((error) => console.error('End conversation error:', error));
    }
    setMessages([]);
    setSessionId(null);
    startConversation();
  };

  // Send text message
  const sendMessage = async (text, source = 'text', options = {}) => {
    if (!text.trim() || !sessionId) return;

    // Add user message immediately (unless this is a retry of a turn already on screen)
    if (!options.isRetry) {
      setMessages(prev => [...prev, { role: 'user', content: text, source }]);
      setInputText('');
    }
    setFailure(null);
    setIsLoading(true);

    try {
      const response = await fetchWithAuthRetry(`${API_URL}/api/conversation/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          source,
          lang
        })
      });

      if (!response.ok) throw new Error(`message failed (${response.status})`);
      
      const data = await response.json();
      
      // Add Arvexis response
      setMessages(prev => [...prev, { role: 'arvexis', content: data.message }]);
      setLastAttempt(null);
      
      // Check if conversation is complete
      if (data.conversation_complete) {
        // Arvexis asked if there's anything unresolved
        // We'll let the user respond naturally
      }
      
      // Handle detected insights (Phase 2)
      if (data.detected_insights?.length > 0) {
        // Will show insight modal in Phase 2
      }
    } catch (error) {
      console.error('Send message error:', error);
      setFailure('message');
      setLastAttempt({ type: 'message', text, source });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    // The turn is sent only from here — an explicit Send — whether the words were typed or
    // spoken. Text that came from a transcript is still their voice, so it is recorded as such.
    sendMessage(inputText, composerFromVoice ? 'voice' : 'text');
    setComposerFromVoice(false);
  };

  // Voice recording handlers
  const cleanupAudioAnalysis = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {}
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  };

  const startRecording = async () => {
    setFailure(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recorder._stream = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      // Audio analysis for the live waveform — identical metering to the Message composer.
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.8;

        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        const updateLevel = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(dataArray);
          const sum = dataArray.reduce((a, b) => a + b, 0);
          setAudioLevel(sum / dataArray.length / 255);
          animationFrameRef.current = requestAnimationFrame(updateLevel);
        };
        updateLevel();
      } catch (e) {
        console.log('Audio analysis not available:', e);
      }

      recorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      // Set on stop, not here: whether the audio is transcribed into the composer or thrown
      // away is decided by which control the member pressed.
      recorder.start();
      setIsRecording(true);
      setRecordingPhase('recording');
    } catch (error) {
      // A blocked or missing microphone must say so rather than look like a dead button.
      console.error('Recording error:', error);
      cleanupAudioAnalysis();
      setIsRecording(false);
      setRecordingPhase(null);
      setFailure('mic');
      setLastAttempt(null);
    }
  };

  // Stop recording and transcribe INTO THE COMPOSER for review — never send.
  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || !isRecording) return;

    cleanupAudioAnalysis();

    const stream = recorder._stream;
    recorder.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      audioChunksRef.current = [];
      if (stream) stream.getTracks().forEach(track => track.stop());
      await transcribeToComposer(audioBlob);
    };

    recorder.stop();
    setIsRecording(false);
    setRecordingPhase('transcribing');
  };

  // Discard the recording outright — nothing is transcribed and nothing reaches the composer.
  const cancelRecording = () => {
    cleanupAudioAnalysis();
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      recorder.onstop = null;
      if (recorder.state !== 'inactive') recorder.stop();
      if (recorder._stream) recorder._stream.getTracks().forEach(track => track.stop());
    }
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setRecordingPhase(null);
  };

  const transcribeToComposer = async (audioBlob) => {
    setFailure(null);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      // This panel is dream voice entry, so dream conditioning is appropriate here.
      formData.append('context', 'dream');

      const response = await fetchWithAuthRetry(`${API_URL}/api/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error(`transcription failed (${response.status})`);

      const data = await response.json();
      const spoken = (data.text || '').trim();
      if (!spoken) throw new Error('empty transcription');

      // Into the composer, where they can read it, change it or clear it.
      setInputText(spoken);
      setComposerFromVoice(true);
      setRecordingPhase(null);
      inputRef.current?.focus();
    } catch (error) {
      console.error('Transcription error:', error);
      setRecordingPhase(null);
      setFailure('voice');
      setLastAttempt(null);
    }
  };
  
  const handleRetry = () => {
    if (failure === 'start') {
      startConversation();
      return;
    }
    if (lastAttempt?.type === 'message') {
      sendMessage(lastAttempt.text, lastAttempt.source, { isRetry: true });
      return;
    }
    setFailure(null);
  };

  // Pause conversation
  const handlePause = async () => {
    stopListen();
    if (sessionId) {
      await fetchWithAuthRetry(`${API_URL}/api/conversation/pause/${sessionId}`, {
        method: 'POST',
        credentials: 'include'
      }).catch((error) => console.error('Pause conversation error:', error));
    }
    onClose?.();
  };

  // End conversation
  const handleEnd = async () => {
    if (!sessionId) return;
    setFailure(null);
    try {
      const response = await fetchWithAuthRetry(`${API_URL}/api/conversation/end/${sessionId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error(`end failed (${response.status})`);
      
      const data = await response.json();
      setMessages(prev => [...prev, { role: 'arvexis', content: data.message }]);
      setPhase('ended');
      onConversationEnd?.(data);
    } catch (error) {
      console.error('End conversation error:', error);
      setFailure('end');
      setLastAttempt(null);
    }
  };

  return (
    <div 
      className="flex flex-col h-full"
      style={{ background: 'linear-gradient(180deg, #0B0C13 0%, #07080D 100%)' }}
      data-testid="dream-conversation"
    >
      {/* Header */}
      <header 
        className="flex items-center justify-between px-4 py-3"
        style={{ 
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(11,12,19,0.96)'
        }}
      >
        <div className="flex items-center gap-2">
          <ArvexisSymbol size={20} />
          <span 
            className="text-sm text-[#F4EFE7]/80"
            style={{ fontFamily: '"Inter", sans-serif' }}
          >
            {lang === 'es' ? 'Conversación' : 'Conversation'}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {phase === 'active' && (
            <button
              onClick={handlePause}
              className="p-2 rounded-lg text-[#F4EFE7]/50 hover:text-[#F4EFE7] hover:bg-white/5 transition-all"
              title={lang === 'es' ? 'Pausar' : 'Pause'}
              data-testid="conversation-pause"
            >
              <Pause size={18} />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-[#F4EFE7]/50 hover:text-[#F4EFE7] hover:bg-white/5 transition-all"
            data-testid="conversation-close"
          >
            <X size={18} />
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <AnimatePresence mode="wait">
          {/* Initial prompt */}
          {phase === 'prompt' && (
            <>
              <ExplorePrompt 
                lang={lang}
                onExplore={handleExplore}
                onMaybeLater={handleMaybeLater}
              />
              {failure === 'start' && !isLoading && (
                <div className="px-6 pb-6 text-center" data-testid="conversation-error">
                  <p className="text-xs text-[#F4EFE7]/45 mb-2" style={{ fontFamily: '"Inter", sans-serif' }}>
                    {lang === 'es'
                      ? 'No pude abrir la conversación. Tu reflexión sigue aquí.'
                      : "I couldn't open the conversation. Your reflection is still here."}
                  </p>
                  <button
                    onClick={handleRetry}
                    className="text-xs text-[#E8C58F]/85 hover:text-[#E8C58F] transition-colors"
                    data-testid="conversation-retry-btn"
                  >
                    {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Resume prompt */}
          {phase === 'resume' && (
            <ResumePrompt
              lang={lang}
              message={resumeMessage}
              onContinue={handleContinue}
              onStartFresh={handleStartFresh}
            />
          )}

          {/* Active conversation */}
          {(phase === 'active' || phase === 'ended') && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="px-4 py-6"
            >
              {messages.map((msg, index) => (
                <MessageBubble
                  key={index}
                  message={msg}
                  isArvexis={msg.role === 'arvexis'}
                  lang={lang}
                  onListen={msg.role === 'arvexis' ? () => speakArvexis(msg.content, index) : undefined}
                  isSpeaking={speakingIndex === index}
                  isPreparing={preparingIndex === index}
                />
              ))}
              
              {isLoading && <TypingIndicator />}

              {/* Read-aloud failure is said plainly, in place, without touching the conversation */}
              {listenFailed && (
                <p
                  className="px-4 pb-2 text-xs text-[#F4EFE7]/45"
                  style={{ fontFamily: '"Inter", sans-serif' }}
                  data-testid="dream-conversation-listen-error"
                >
                  {lang === 'es'
                    ? 'No pude leerlo en voz alta ahora mismo. Puedes intentarlo de nuevo.'
                    : "I couldn't read that aloud just now. You can try again."}
                </p>
              )}
              
              {/* Honest failure notice with a retry — never a silent dead turn */}
              {failure && !isLoading && (
                <div 
                  className="flex flex-col items-center text-center gap-2 px-4 py-4 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="conversation-error"
                >
                  <p className="text-xs text-[#F4EFE7]/45" style={{ fontFamily: '"Inter", sans-serif' }}>
                    {failure === 'mic'
                      ? (lang === 'es'
                          ? 'No pude acceder al micrófono. Revisa el permiso o escríbelo.'
                          : "I couldn't reach your microphone. Check the permission, or type it instead.")
                      : failure === 'voice'
                        ? (lang === 'es'
                            ? 'No pude escuchar esa grabación. Puedes intentarlo otra vez o escribirlo.'
                            : "I couldn't hear that recording. You can try again or type it instead.")
                        : (lang === 'es'
                            ? 'Se interrumpió la conexión. Tu mensaje no se perdió.'
                            : "The connection was interrupted. Your message wasn't lost.")}
                  </p>
                  {(failure === 'start' || lastAttempt?.type === 'message') && (
                    <button
                      onClick={handleRetry}
                      className="text-xs text-[#E8C58F]/85 hover:text-[#E8C58F] transition-colors"
                      data-testid="conversation-retry-btn"
                    >
                      {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
                    </button>
                  )}
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Input area (only in active phase) */}
      {phase === 'active' && (
        <div 
          className="px-4 py-4"
          style={{ 
            borderTop: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(11,12,19,0.98)'
          }}
        >
          {/* =================================================================
              INLINE COMPOSER — Message composer is the canonical UX reference.
              Idle → Recording → Transcribing → Editable transcript → explicit Send.
              The composer itself becomes the recording surface; the single right-hand
              action slot transforms Mic → Stop in place. Dream's own surface treatment
              (gold Send, dream panel, dream copy) is preserved.
              ================================================================= */}
          <form onSubmit={handleSubmit} className="flex items-end gap-2">
            <div className="flex-1 relative">
              <AnimatePresence mode="wait">
                {/* RECORDING STATE — waveform replaces the text field */}
                {isRecording && (
                  <motion.div
                    key="recording"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="w-full px-3 py-2 rounded-2xl flex items-center gap-2"
                    style={{
                      minHeight: '42px',
                      background: 'rgba(180, 140, 255, 0.06)',
                      border: '1px solid rgba(180, 140, 255, 0.15)',
                    }}
                    data-testid="conversation-recording-state"
                  >
                    {/* Discard — subtle X at left, inside the pill */}
                    <button
                      type="button"
                      onClick={cancelRecording}
                      className="p-1.5 rounded-full text-[#F4EFE7]/30 hover:text-[#F4EFE7]/50 hover:bg-white/5 transition-colors flex-shrink-0"
                      aria-label={lang === 'es' ? 'Descartar grabación' : 'Discard recording'}
                      data-testid="conversation-voice-cancel-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>

                    <div className="flex-1 flex items-center justify-center">
                      <VoiceWaveform audioLevel={audioLevel} isActive={true} />
                    </div>
                  </motion.div>
                )}

                {/* TRANSCRIBING STATE — calm progress, no status chatter */}
                {recordingPhase === 'transcribing' && !isRecording && (
                  <motion.div
                    key="transcribing"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="w-full px-4 py-2.5 rounded-2xl flex items-center justify-center gap-3"
                    style={{
                      minHeight: '42px',
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                    data-testid="conversation-transcribing-state"
                  >
                    <Loader2 className="w-4 h-4 text-[#F4EFE7]/40 animate-spin" />
                    <span className="text-[#F4EFE7]/50 text-sm">
                      {activityLabel(ACTIVITY.TRANSCRIBING, lang)}
                    </span>
                  </motion.div>
                )}

                {/* IDLE / EDITABLE STATE — the transcript lands here to be read or changed */}
                {!isRecording && recordingPhase !== 'transcribing' && (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="relative"
                  >
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputText}
                      onChange={(e) => {
                        setInputText(e.target.value);
                        if (!e.target.value.trim()) setComposerFromVoice(false);
                      }}
                      placeholder={lang === 'es' ? 'Escribe tu respuesta...' : 'Type your response...'}
                      disabled={isLoading}
                      className="w-full px-4 py-2.5 rounded-2xl focus:outline-none transition-colors placeholder-white/30"
                      style={{
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        // Kept at 16px on purpose: 14px triggers iOS Safari focus zoom.
                        fontSize: '16px',
                        minHeight: '42px',
                        lineHeight: 1.6,
                        background: 'rgba(255, 255, 255, 0.04)',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                        color: 'rgba(255, 255, 255, 0.85)'
                      }}
                      data-testid="conversation-input"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* IDLE STATE: Mic in the single right-hand action slot */}
            {!isRecording && recordingPhase !== 'transcribing' && !inputText.trim() && (
              <button
                type="button"
                onClick={startRecording}
                disabled={isLoading}
                className="p-2.5 rounded-full transition-all text-[#F4EFE7]/30 hover:text-[#F4EFE7]/50"
                aria-label={lang === 'es' ? 'Grabar' : 'Record'}
                data-testid="conversation-voice-btn"
              >
                <Mic className="w-5 h-5" />
              </button>
            )}

            {/* RECORDING STATE: Stop replaces Mic in the same position */}
            {isRecording && (
              <button
                type="button"
                onClick={stopRecording}
                className="p-2.5 rounded-full transition-all text-[#F4EFE7]/70 bg-white/10 hover:bg-white/15"
                aria-label={lang === 'es' ? 'Detener grabación' : 'Stop recording'}
                data-testid="conversation-voice-stop-btn"
              >
                <Square className="w-4 h-4 fill-current" />
              </button>
            )}

            {/* Send — the only thing that transmits a turn, typed or spoken */}
            {!isRecording && recordingPhase !== 'transcribing' && inputText.trim() && (
              <button
                type="submit"
                disabled={!inputText.trim() || isLoading}
                className="p-2.5 rounded-full transition-all disabled:opacity-30"
                style={{
                  background: 'rgba(232, 197, 143, 0.06)', border: '1px solid rgba(232, 197, 143, 0.56)'
                }}
                data-testid="conversation-send-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[#E8C58F]" />
                ) : (
                  <Send className="w-5 h-5 text-[#E8C58F]" />
                )}
              </button>
            )}

            {/* TRANSCRIBING STATE: loading sits in the action slot */}
            {recordingPhase === 'transcribing' && !isRecording && (
              <div className="p-2.5">
                <Loader2 className="w-5 h-5 text-[#F4EFE7]/30 animate-spin" />
              </div>
            )}
          </form>

          {/* End conversation link */}
          <div className="flex justify-center mt-3">
            <button
              onClick={handleEnd}
              className="text-xs text-[#F4EFE7]/30 hover:text-[#F4EFE7]/50 transition-colors"
              style={{ fontFamily: '"Inter", sans-serif' }}
              data-testid="conversation-end-btn"
            >
              {lang === 'es' ? 'Terminar conversación' : 'End conversation'}
            </button>
          </div>
        </div>
      )}

      {/* Ended state */}
      {phase === 'ended' && (
        <div 
          className="px-4 py-6 text-center"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-full text-sm text-[#F4EFE7]/70 hover:text-[#F4EFE7] transition-all"
            style={{ 
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            {lang === 'es' ? 'Volver al sueño' : 'Return to Dream'}
          </button>
        </div>
      )}
    </div>
  );
};

export default DreamConversation;
